import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, HardHat, ShieldCheck, Users, ArrowRight, Banknote, FileCheck, Home as HomeIcon, Calendar, DollarSign } from "lucide-react";
import FAQ from "@/components/FAQ";
import RecentWins from "@/components/RecentWins";
import LeadFormModal from "@/components/LeadFormModal";
import CreativeFinancingForm from "@/components/CreativeFinancingForm";
import { Link } from "wouter";
import { useState } from "react";

export default function Home() {
  const [address, setAddress] = useState("");

  return (
    <div className="flex flex-col gap-0">
      {/* Hero Section - Forest Green */}
      <section className="relative bg-[oklch(0.2_0.08_145)] text-white overflow-hidden">
        {/* Background Image with Overlay - WebP with JPG fallback */}
        <div className="absolute inset-0 z-0">
          <picture>
            <source 
              media="(max-width: 768px)" 
              srcSet="/hero-home-mobile.webp" 
              type="image/webp" 
            />
            <source 
              media="(max-width: 768px)" 
              srcSet="/hero-home-mobile.jpg" 
              type="image/jpeg" 
            />
            <source 
              srcSet="/hero-home.webp" 
              type="image/webp" 
            />
            <img 
              src="/hero-home.jpg" 
              alt="Modern Calgary Home Renovation" 
              className="w-full h-full object-cover object-center opacity-40"
              fetchPriority="high"
              loading="eager"
              decoding="async"
              width="1400"
              height="933"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-r from-[oklch(0.15_0.08_145)] via-[oklch(0.18_0.08_145)/85%] to-[oklch(0.18_0.08_145)/60%]"></div>
          {/* Bottom fade into stats section */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[oklch(0.25_0.1_145)] to-transparent"></div>
        </div>

        <div className="container relative z-10 py-20 md:py-32 grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6" style={{ animation: 'slideInLeft 0.7s ease-out', transform: 'translateZ(0)' }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[oklch(0.25_0.1_145)] border border-[oklch(0.75_0.15_85)] text-[oklch(0.92_0.12_85)] text-sm font-medium">
              <HardHat className="w-4 h-4" />
              <span>Direct Buyers • Renovators & Builders</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight text-white">
              Let's Find Your <br className="hidden md:block" /><span className="text-[oklch(0.75_0.15_85)]">Best Path Forward</span>
            </h1>
            <p className="text-lg md:text-xl text-[oklch(0.92_0.02_85)] max-w-xl leading-relaxed">
              Every homeowner's situation is different. Whether you need a quick sale, want to list for top dollar, or have a complex situation—we're here to help you find the option that makes the most sense for you.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <div className="flex items-center gap-2 text-[oklch(0.92_0.02_85)]">
                <CheckCircle2 className="w-5 h-5 text-[oklch(0.75_0.15_85)]" />
                <span>Direct Offers</span>
              </div>
              <div className="flex items-center gap-2 text-[oklch(0.92_0.02_85)]">
                <CheckCircle2 className="w-5 h-5 text-[oklch(0.75_0.15_85)]" />
                <span>Listing Options</span>
              </div>
              <div className="flex items-center gap-2 text-[oklch(0.92_0.02_85)]">
                <CheckCircle2 className="w-5 h-5 text-[oklch(0.75_0.15_85)]" />
                <span>Creative Solutions</span>
              </div>
            </div>
          </div>

          {/* Simplified Address-Only Form Card */}
          <div className="w-full max-w-md ml-auto" style={{ animation: 'slideInRight 0.7s ease-out 0.1s both', transform: 'translateZ(0)' }}>
            <Card className="border-0 shadow-2xl bg-white">
              <CardContent className="p-8 space-y-6">
                <div className="text-center space-y-2">
                  <h2 className="text-2xl font-serif font-bold text-[oklch(0.25_0.1_145)]">Let's Talk</h2>
                  <p className="text-[oklch(0.4_0.03_145)]">Tell us about your property and situation</p>
                </div>
                
                <div className="space-y-4">
                  <input
                    type="text"
                    name="propertyAddress"
                    autoComplete="street-address"
                    inputMode="text"
                    aria-label="Property address"
                    placeholder="Enter your property address..."
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="w-full px-4 py-4 text-lg border-2 border-[oklch(0.9_0.02_85)] rounded-lg focus:border-[oklch(0.75_0.15_85)] focus:outline-none focus:ring-2 focus:ring-[oklch(0.75_0.15_85)/20%] transition-all"
                  />
                  
                  <LeadFormModal 
                    source="homepage-hero"
                    prefilledAddress={address}
                    trigger={
                      <Button className="w-full bg-[oklch(0.75_0.15_85)] hover:bg-[oklch(0.65_0.15_85)] text-[oklch(0.2_0.03_145)] font-bold text-lg py-6 h-auto shadow-lg hover:shadow-xl transition-all group">
                        Start the Conversation
                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    }
                  />
                </div>

                <div className="flex items-center justify-center gap-6 pt-2 text-sm text-[oklch(0.4_0.03_145)]">
                  <div className="flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4 text-[oklch(0.45_0.12_145)]" />
                    <span>Free</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4 text-[oklch(0.45_0.12_145)]" />
                    <span>No Pressure</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4 text-[oklch(0.45_0.12_145)]" />
                    <span>Your Options</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Trust Bar - Gold Accent - Blends from hero */}
      <section className="bg-[oklch(0.25_0.1_145)] py-12 border-b-4 border-[oklch(0.75_0.15_85)] -mt-1">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="space-y-1">
              <p className="text-4xl md:text-5xl font-bold text-white">500+</p>
              <p className="text-[oklch(0.75_0.15_85)] text-sm uppercase tracking-wider font-medium">Projects Completed</p>
            </div>
            <div className="space-y-1">
              <p className="text-4xl md:text-5xl font-bold text-white">100%</p>
              <p className="text-[oklch(0.75_0.15_85)] text-sm uppercase tracking-wider font-medium">Direct Buyer</p>
            </div>
            <div className="space-y-1">
              <p className="text-4xl md:text-5xl font-bold text-white">0</p>
              <p className="text-[oklch(0.75_0.15_85)] text-sm uppercase tracking-wider font-medium">Conditions</p>
            </div>
            <div className="space-y-1">
              <p className="text-4xl md:text-5xl font-bold text-white">Same Day</p>
              <p className="text-[oklch(0.75_0.15_85)] text-sm uppercase tracking-wider font-medium">Offer On Site</p>
            </div>
          </div>
        </div>
      </section>

      {/* Your Journey, Your Choice - MOVED UP */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[oklch(0.2_0.03_145)]">Your Journey, Your Choice</h2>
            <p className="text-lg text-[oklch(0.4_0.03_145)] max-w-2xl mx-auto">
              We start by understanding your situation, then help you explore the options that make the most sense for you.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-12 left-0 w-full h-0.5 bg-[oklch(0.9_0.02_85)] -z-10"></div>

            {/* Step 1 */}
            <div className="bg-white p-6 text-center space-y-4">
              <div className="w-24 h-24 mx-auto bg-white border-4 border-[oklch(0.92_0.05_145)] rounded-full flex items-center justify-center text-3xl font-serif font-bold text-[oklch(0.35_0.12_145)] shadow-sm">
                1
              </div>
              <h3 className="text-xl font-bold text-[oklch(0.2_0.03_145)]">We Listen</h3>
              <p className="text-[oklch(0.4_0.03_145)]">
                Tell us about your situation—your timeline, your goals, what matters most to you. This isn't a sales pitch, it's a conversation.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-white p-6 text-center space-y-4">
              <div className="w-24 h-24 mx-auto bg-white border-4 border-[oklch(0.75_0.15_85)] rounded-full flex items-center justify-center text-3xl font-serif font-bold text-[oklch(0.35_0.12_145)] shadow-sm">
                2
              </div>
              <h3 className="text-xl font-bold text-[oklch(0.2_0.03_145)]">We Explore Options</h3>
              <p className="text-[oklch(0.4_0.03_145)]">
                Based on your needs, we'll discuss the paths available—direct offer, listing with a realtor, or a creative solution.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-white p-6 text-center space-y-4">
              <div className="w-24 h-24 mx-auto bg-white border-4 border-[oklch(0.92_0.05_145)] rounded-full flex items-center justify-center text-3xl font-serif font-bold text-[oklch(0.35_0.12_145)] shadow-sm">
                3
              </div>
              <h3 className="text-xl font-bold text-[oklch(0.2_0.03_145)]">You Decide</h3>
              <p className="text-[oklch(0.4_0.03_145)]">
                Take your time. No pressure. When you're ready, we execute whichever path you choose—or we part as friends.
              </p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link href="/your-path-forward">
              <Button variant="outline" className="border-[oklch(0.35_0.12_145)] text-[oklch(0.35_0.12_145)] hover:bg-[oklch(0.95_0.05_145)] font-bold px-8">
                Learn About Your Path Forward
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Two Ways to Sell Section - MOVED UP */}
      <section className="py-20 bg-[oklch(0.97_0.01_85)]">
        <div className="container max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[oklch(0.2_0.03_145)]">
              Two Ways to Sell Your Calgary Home
            </h2>
            <p className="text-lg text-[oklch(0.4_0.03_145)] mt-4 max-w-2xl mx-auto">
              We offer options based on your timeline and priorities. Need to sell fast? We'll buy it directly. Have time to wait? We can list it for you.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Option 1: Direct Purchase */}
            <Card className="border-2 border-[oklch(0.75_0.15_85)] shadow-xl relative overflow-hidden bg-white">
              <div className="absolute top-0 right-0 bg-[oklch(0.75_0.15_85)] text-[oklch(0.2_0.03_145)] px-4 py-1 text-sm font-bold">
                Most Popular
              </div>
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-[oklch(0.92_0.08_85)] rounded-full flex items-center justify-center mb-6">
                  <HomeIcon className="w-7 h-7 text-[oklch(0.55_0.15_85)]" />
                </div>
                <h3 className="text-2xl font-bold text-[oklch(0.2_0.03_145)] mb-2">We Buy It Directly</h3>
                <p className="text-[oklch(0.5_0.03_145)] mb-6">Best for homeowners who need speed, certainty, or want to avoid the hassle of listing.</p>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[oklch(0.45_0.15_145)] shrink-0 mt-0.5" />
                    <span className="text-[oklch(0.3_0.03_145)]"><strong>Close in 7-90 days</strong> — you pick the date</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[oklch(0.45_0.15_145)] shrink-0 mt-0.5" />
                    <span className="text-[oklch(0.3_0.03_145)]"><strong>Unconditional offer</strong> — no financing or inspection outs</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[oklch(0.45_0.15_145)] shrink-0 mt-0.5" />
                    <span className="text-[oklch(0.3_0.03_145)]"><strong>Sell as-is</strong> — no repairs, no cleaning, no staging</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[oklch(0.45_0.15_145)] shrink-0 mt-0.5" />
                    <span className="text-[oklch(0.3_0.03_145)]"><strong>Zero fees</strong> — no commissions, no hidden costs</span>
                  </li>
                </ul>
                
                <LeadFormModal 
                  source="homepage-direct-offer"
                  trigger={
                    <Button className="w-full bg-[oklch(0.75_0.15_85)] hover:bg-[oklch(0.65_0.15_85)] text-[oklch(0.2_0.03_145)] font-bold py-6 h-auto">
                      Get Your Direct Offer
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  }
                />
              </CardContent>
            </Card>
            
            {/* Option 2: List with Partner Realtor */}
            <Card className="border-0 shadow-xl bg-white">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-[oklch(0.92_0.05_145)] rounded-full flex items-center justify-center mb-6">
                  <Users className="w-7 h-7 text-[oklch(0.35_0.12_145)]" />
                </div>
                <h3 className="text-2xl font-bold text-[oklch(0.2_0.03_145)] mb-2">List with Our Partner Realtors</h3>
                <p className="text-[oklch(0.5_0.03_145)] mb-6">Best for homeowners who have time and want to potentially get top market value.</p>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[oklch(0.45_0.15_145)] shrink-0 mt-0.5" />
                    <span className="text-[oklch(0.3_0.03_145)]"><strong>Potential for higher price</strong> — market exposure may yield more</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[oklch(0.45_0.15_145)] shrink-0 mt-0.5" />
                    <span className="text-[oklch(0.3_0.03_145)]"><strong>Trusted local realtors</strong> — we only partner with the best</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[oklch(0.45_0.15_145)] shrink-0 mt-0.5" />
                    <span className="text-[oklch(0.3_0.03_145)]"><strong>Full MLS exposure</strong> — maximum buyer reach</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-[oklch(0.6_0.08_85)] shrink-0 mt-0.5" />
                    <span className="text-[oklch(0.4_0.03_145)]">Typically 60-120 days to close</span>
                  </li>
                </ul>
                
                <LeadFormModal 
                  source="homepage-listing-option"
                  modalTitle="Let's Explore Listing Options"
                  modalDescription="Tell us about your property and we'll connect you with our trusted realtor partners."
                  isListingInquiry={true}
                  trigger={
                    <Button variant="outline" className="w-full border-[oklch(0.35_0.12_145)] text-[oklch(0.35_0.12_145)] hover:bg-[oklch(0.92_0.05_145)] font-bold py-6 h-auto">
                      Explore Listing Options
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  }
                />
              </CardContent>
            </Card>
          </div>
          
          <p className="text-center text-[oklch(0.5_0.03_145)] mt-8 max-w-2xl mx-auto">
            Not sure which option is right for you? Reach out and we'll discuss your situation—no pressure, just honest advice on what makes sense for your timeline and goals.
          </p>
        </div>
      </section>

      {/* Creative Financing Section */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[oklch(0.25_0.1_145)] text-white text-sm font-medium">
                  <Banknote className="w-4 h-4" />
                  <span>Flexible Solutions</span>
                </div>
                <h2 className="text-3xl font-serif font-bold text-[oklch(0.2_0.03_145)]">
                  Creative Financing Options
                </h2>
                <p className="text-lg text-[oklch(0.4_0.03_145)] leading-relaxed">
                  Need to clear debt before closing? Want a leaseback arrangement? We can structure offers to fit your unique situation. Our flexibility is another advantage of being direct buyers with our own capital.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-[oklch(0.3_0.03_145)]">
                    <CheckCircle2 className="w-5 h-5 text-[oklch(0.35_0.12_145)]" />
                    <span>Debt payoff arrangements</span>
                  </li>
                  <li className="flex items-center gap-3 text-[oklch(0.3_0.03_145)]">
                    <CheckCircle2 className="w-5 h-5 text-[oklch(0.35_0.12_145)]" />
                    <span>Flexible closing timelines</span>
                  </li>
                  <li className="flex items-center gap-3 text-[oklch(0.3_0.03_145)]">
                    <CheckCircle2 className="w-5 h-5 text-[oklch(0.35_0.12_145)]" />
                    <span>Leaseback options available</span>
                  </li>
                </ul>
              </div>
              <div className="bg-[oklch(0.97_0.01_85)] p-8 rounded-2xl shadow-lg">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-[oklch(0.92_0.05_145)] rounded-full flex items-center justify-center">
                    <FileCheck className="w-7 h-7 text-[oklch(0.35_0.12_145)]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[oklch(0.2_0.03_145)]">Tailored Offers</h3>
                    <p className="text-sm text-[oklch(0.4_0.03_145)]">Every situation is different</p>
                  </div>
                </div>
                <p className="text-[oklch(0.4_0.03_145)] mb-6">
                  Unlike cookie-cutter cash offer companies, we take the time to understand your needs and structure an offer that works for you.
                </p>
                <Link href="/creative-financing">
                  <Button className="w-full bg-[oklch(0.75_0.15_85)] hover:bg-[oklch(0.65_0.15_85)] text-[oklch(0.2_0.03_145)] font-bold">
                    Explore Creative Financing <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Truth About Wholesaling Section - MOVED DOWN */}
      <section className="py-20 bg-[oklch(0.97_0.01_85)]">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[oklch(0.2_0.03_145)]">What "Cash Offer" Companies Won't Tell You</h2>
            <p className="text-lg text-[oklch(0.4_0.03_145)] leading-relaxed">
              Most "We Buy Houses" companies are actually marketing firms. They don't buy your house—they sell the <em>contract</em> for an assignment fee to actual builders and renovators (like us). We cut out the middleman.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* The Wholesaler Way */}
            <Card className="border border-[oklch(0.9_0.02_85)] shadow-sm hover:shadow-md transition-shadow bg-white">
              <CardContent className="p-8 space-y-6">
                <div className="w-12 h-12 bg-[oklch(0.95_0.01_85)] rounded-full flex items-center justify-center text-[oklch(0.4_0.03_145)] mb-4">
                  <Users className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-[oklch(0.2_0.03_145)]">The "Cash Offer Company" Model</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3 text-[oklch(0.4_0.03_145)]">
                    <span className="text-red-500 font-bold">×</span>
                    <span>They get your home under contract for a low price.</span>
                  </li>
                  <li className="flex items-start gap-3 text-[oklch(0.4_0.03_145)]">
                    <span className="text-red-500 font-bold">×</span>
                    <span>They shop your contract around to find a real buyer.</span>
                  </li>
                  <li className="flex items-start gap-3 text-[oklch(0.4_0.03_145)]">
                    <span className="text-red-500 font-bold">×</span>
                    <span>They charge a $10k-$25k "assignment fee" that comes out of your pocket.</span>
                  </li>
                  <li className="flex items-start gap-3 text-[oklch(0.4_0.03_145)]">
                    <span className="text-red-500 font-bold">×</span>
                    <span><strong>Offers have conditions</strong>—they can back out if they can't find a buyer.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Our Way */}
            <Card className="border-2 border-[oklch(0.35_0.12_145)] shadow-lg relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-[oklch(0.75_0.15_85)] text-[oklch(0.2_0.03_145)] text-xs font-bold px-3 py-1 rounded-bl-lg">
                DIRECT BUYER
              </div>
              <CardContent className="p-8 space-y-6 bg-[oklch(0.95_0.05_145)/30%]">
                <div className="w-12 h-12 bg-[oklch(0.92_0.05_145)] rounded-full flex items-center justify-center text-[oklch(0.35_0.12_145)] mb-4">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-[oklch(0.25_0.1_145)]">The Direct Buyer Model (Us)</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3 text-[oklch(0.3_0.03_145)]">
                    <CheckCircle2 className="w-5 h-5 text-[oklch(0.35_0.12_145)] shrink-0" />
                    <span><strong>We are the actual buyer.</strong> Local renovators and builders with our own funding.</span>
                  </li>
                  <li className="flex items-start gap-3 text-[oklch(0.3_0.03_145)]">
                    <CheckCircle2 className="w-5 h-5 text-[oklch(0.35_0.12_145)] shrink-0" />
                    <span><strong>Unconditional offers.</strong> No financing conditions, no inspection outs—we commit.</span>
                  </li>
                  <li className="flex items-start gap-3 text-[oklch(0.3_0.03_145)]">
                    <CheckCircle2 className="w-5 h-5 text-[oklch(0.35_0.12_145)] shrink-0" />
                    <span><strong>Same-day pricing.</strong> Get your offer during our first visit, not 24-48 hours later.</span>
                  </li>
                  <li className="flex items-start gap-3 text-[oklch(0.3_0.03_145)]">
                    <CheckCircle2 className="w-5 h-5 text-[oklch(0.35_0.12_145)] shrink-0" />
                    <span><strong>Creative financing available.</strong> Need to clear debt? We can tailor offers to your situation.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ />

      {/* Recent Wins Section */}
      <RecentWins />

      {/* Final CTA - Forest Green */}
      <section className="py-20 bg-[oklch(0.25_0.1_145)] text-white text-center">
        <div className="container max-w-3xl mx-auto space-y-8">
          <h2 className="text-3xl md:text-4xl font-serif font-bold">
            Let's Start the Conversation
          </h2>
          <p className="text-xl text-[oklch(0.8_0.03_85)]">
            Whether you're ready to move forward or just exploring your options, we're here to help you figure out what makes the most sense for your situation.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <LeadFormModal 
              source="homepage-cta"
              trigger={
                <Button className="bg-[oklch(0.75_0.15_85)] hover:bg-[oklch(0.65_0.15_85)] text-[oklch(0.2_0.03_145)] font-bold text-lg px-8 py-6 h-auto shadow-lg">
                  Start the Conversation
                </Button>
              }
            />
            <a 
              href="tel:3062690254"
              onClick={() => {
                if (typeof window !== 'undefined' && (window as any).gtag_report_phone_conversion) {
                  (window as any).gtag_report_phone_conversion();
                }
              }}
            >
              <Button variant="outline" className="border-white text-white hover:bg-white/10 font-bold text-lg px-8 py-6 h-auto">
                Call (306) 269-0254
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
